import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Terminal, CheckCircle, ArrowRight } from 'lucide-react';
import { TypeAnimation } from 'react-type-animation';
export const OurPackages = () => {
  const [activeTab, setActiveTab] = useState('starter');
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      const element = document.getElementById('packages');
      if (element) {
        const position = element.getBoundingClientRect();
        if (position.top < window.innerHeight - 100) {
          setIsVisible(true);
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on mount
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const packages = {
    starter: {
      name: 'Starter Package',
      description: 'Perfect for small businesses looking to boost sales calls',
      features: ['Experienced Virtual Assistant', 'Client Success Manager', 'Weekly Performance Reports', 'Readymode Dialer', 'Choose Your Virtual Assistant', 'Quality Control'],
      recommended: false
    },
    professional: {
      name: 'Professional Package',
      description: 'Ideal for growing businesses with established sales teams',
      features: ['Experienced Virtual Assistant', 'Client Success Manager', 'Weekly Performance Reports', 'Readymode Dialer', 'Choose Your Virtual Assistant', 'Quality Control', 'Data Pulling 15k Records', 'Data Skiptracing 15k Records'],
      recommended: true
    },
    ultimate: {
      name: 'Ultimate Package',
      description: 'Comprehensive solution for large organizations',
      features: ['2 Experienced Virtual Assistants', 'Client Success Manager', 'Weekly Performance Reports', 'Readymode Dialer', 'Choose Your Virtual Assistant', 'Quality Control', 'Data Pulling 30k Records', 'Data Skiptracing 30k Records'],
      recommended: false
    },
    custom: {
      name: 'Custom Solution',
      description: 'Tailored specifically to your unique business requirements',
      customText: "Our Custom Package is built entirely around you. Unlike standard plans, this option is fully tailored to your specific goals, preferences, and business needs. You have complete flexibility to decide what's included, ensuring that every detail works in your favor. In other words, the package is designed by you and for you giving you a solution that's as unique as your business.",
      recommended: false
    }
  };
  const handleContactClick = () => {
    // Open Calendly link directly
    window.open('https://calendly.com/callvanasolutions-info/30min', '_blank');
  };
  return <section id="packages" className="relative py-24 bg-black w-full overflow-hidden" data-id="element-240">
      {/* Animated Grid Background */}
      <div className="absolute inset-0 z-0" data-id="element-241">
        {/* Horizontal grid lines */}
        <div className="absolute inset-0 grid grid-cols-1 grid-rows-12" data-id="element-242">
          {[...Array(12)].map((_, i) => <div key={`h-line-${i}`} className="w-full h-px bg-blue-primary/10" data-id="element-243"></div>)}
        </div>
        {/* Vertical grid lines */}
        <div className="absolute inset-0 grid grid-cols-12 grid-rows-1" data-id="element-244">
          {[...Array(12)].map((_, i) => <div key={`v-line-${i}`} className="h-full w-px bg-blue-primary/10" data-id="element-245"></div>)}
        </div>
        {/* Animated data packets */}
        {[...Array(10)].map((_, i) => <div key={`data-packet-${i}`} className="absolute h-1 bg-gradient-to-r from-transparent via-blue-light to-transparent animate-dataStream opacity-30" style={{
        top: `${Math.random() * 100}%`,
        left: 0,
        animationDelay: `${Math.random() * 8}s`,
        animationDuration: `${5 + Math.random() * 5}s`
      }} data-id="element-246"></div>)}
        {/* Glowing orbs */}
        <div className="absolute top-1/4 left-1/4 w-40 h-40 rounded-full bg-blue-primary/5 animate-orb" data-id="element-247"></div>
        <div className="absolute bottom-1/4 right-1/4 w-60 h-60 rounded-full bg-blue-light/5 animate-orb" style={{
        animationDelay: '10s'
      }} data-id="element-248"></div>
      </div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-249">
        <div className="text-center mb-16" data-id="element-250">
          <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-251">
            Our{' '}
            <span className="text-blue-light relative" data-id="element-252">
              Packages
              <span className="absolute -inset-1 bg-blue-light/10 blur-md -z-10 rounded-lg" data-id="element-253"></span>
            </span>
          </h2>
          <p className="text-gray-300 max-w-2xl mx-auto" data-id="element-254">
            Select the perfect package for your business needs. Each package is
            designed to maximize your ROI and boost sales performance.
          </p>
        </div>
        {/* Terminal-style Tabs */}
        <div className={`flex flex-wrap justify-center mb-8 gap-2 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`} data-id="element-255">
          {Object.keys(packages).map(pkg => <button key={pkg} onClick={() => setActiveTab(pkg)} className={`relative px-6 py-3 rounded-t-md font-mono text-sm flex items-center space-x-2 transition-all duration-300 ${activeTab === pkg ? 'bg-gray-900 text-blue-light border-t border-l border-r border-blue-light/50' : 'bg-gray-900/50 text-gray-400 border-t border-l border-r border-gray-800/50 hover:text-gray-200'}`} data-id="element-256">
              <Terminal size={16} data-id="element-257" />
              <span data-id="element-258">
                {activeTab === pkg ? <TypeAnimation sequence={[`> ${pkg}.exe`]} wrapper="span" cursor={true} repeat={0} data-id="element-259" /> : `> ${pkg}.exe`}
              </span>
              {/* Glowing active indicator */}
              {activeTab === pkg && <motion.div layoutId="active-tab-indicator" className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-light" initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.3
          }} data-id="element-260" />}
            </button>)}
        </div>
        {/* Package Panel */}
        <motion.div key={activeTab} initial={{
        x: 100,
        opacity: 0
      }} animate={{
        x: isVisible ? 0 : 100,
        opacity: isVisible ? 1 : 0
      }} transition={{
        duration: 0.5,
        type: 'spring'
      }} className="bg-gray-900/80 border border-blue-primary/30 rounded-md p-8 max-w-4xl mx-auto relative overflow-hidden" data-id="element-261">
          {/* Scanning effect overlay */}
          <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-light/20 to-transparent" initial={{
          x: -500,
          opacity: 0.5
        }} animate={{
          x: 500,
          opacity: 0
        }} transition={{
          duration: 1.5,
          ease: 'easeInOut'
        }} data-id="element-262" />
          {/* Neon outline glow */}
          <div className="absolute -inset-0.5 bg-gradient-to-r from-blue-primary to-blue-light rounded-md blur opacity-10" data-id="element-263"></div>
          <div className="relative" data-id="element-264">
            {/* Package Header */}
            <div className="mb-6 pb-6 border-b border-gray-800" data-id="element-265">
              <div data-id="element-266">
                <h3 className="text-2xl font-bold text-blue-light mb-2 font-mono" data-id="element-267">
                  <TypeAnimation sequence={[packages[activeTab].name]} wrapper="span" cursor={true} repeat={0} data-id="element-268" />
                </h3>
                <p className="text-gray-400 font-mono" data-id="element-269">
                  <TypeAnimation sequence={[packages[activeTab].description]} wrapper="span" cursor={false} repeat={0} speed={70} data-id="element-270" />
                </p>
              </div>
            </div>
            {/* Features List or Custom Text */}
            <div className="mb-8" data-id="element-271">
              {activeTab !== 'custom' ? <>
                  <h4 className="text-lg font-mono text-blue-light mb-4" data-id="element-272">
                    <TypeAnimation sequence={['>> Features:']} wrapper="span" cursor={true} repeat={0} data-id="element-273" />
                  </h4>
                  <ul className="space-y-3" data-id="element-274">
                    {packages[activeTab].features.map((feature, index) => <motion.li key={index} initial={{
                  opacity: 0,
                  x: -20
                }} animate={{
                  opacity: isVisible ? 1 : 0,
                  x: isVisible ? 0 : -20
                }} transition={{
                  delay: 0.1 * index
                }} className="flex items-start font-mono text-gray-300" data-id="element-275">
                        <CheckCircle size={18} className="mr-3 mt-1 text-blue-light animate-pulse" data-id="element-276" />
                        <TypeAnimation sequence={[
                  // Add a delay before typing starts
                  () => new Promise(resolve => setTimeout(resolve, 500 * index)), feature]} wrapper="span" cursor={false} repeat={0} speed={70} data-id="element-277" />
                      </motion.li>)}
                  </ul>
                </> : <div className="font-mono text-gray-300 leading-relaxed" data-id="element-278">
                  <TypeAnimation sequence={[packages.custom.customText]} wrapper="p" cursor={false} repeat={0} speed={70} data-id="element-279" />
                </div>}
            </div>
            {/* Call to Action */}
            <div className="flex justify-center items-center" data-id="element-280">
              <button onClick={handleContactClick} className="bg-gradient-to-r from-blue-primary to-blue-light hover:from-blue-light hover:to-blue-primary text-white px-6 py-3 rounded font-mono text-sm flex items-center space-x-2 transition-all transform hover:scale-105 group" data-id="element-281">
                <span data-id="element-282">Schedule a Consultation</span>
                <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" data-id="element-283" />
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
};